import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { toast } from 'react-toastify';

import { useInitDataStore } from '../store/initData.store';
import { useItemsStore } from '../store/items.store';
import { useUserStore } from '../store/user.store';
import { useAuthStore } from '../store/authStore.storage';
import { GetInitDataService, InitDataDto } from '../common/services/getInitData.service';
import { ItemDto } from '../common/dtos/initData.dto';
import TokenRefreshModal from '../common/UI/TokenRefreshModal';
import AppGetIcon from '../common/UI/AppGetIcon';
import { PostRefreshTokenService } from '../common/services/postRefreshToken.service';

const postRefreshTokenService = new PostRefreshTokenService();


interface AuthContextType {
  isAuthenticated: boolean;
  userRole: number;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  refreshModal: {
    isOpen: boolean;
    isLoading: boolean;
    timeRemaining: number;
    close: () => void;
    refresh: () => Promise<void>;
    onSessionExpired: () => void;
  };
}


const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider: React.FC<{
  children: ReactNode;
  tokenFromCookie?: string | null;
}> = ({ children, tokenFromCookie = null }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromURL = searchParams.get('token') || tokenFromCookie;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(0);
  const [loading, setLoading] = useState(true);
  // Estados internos para el modal de refresco
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const [modalTimeRemaining, setModalTimeRemaining] = useState<number>(0); // Valor por defecto

  const initDataStore = useInitDataStore((s) => s, shallow);
  const { login: storeLogin, logout: storeLogout } = useAuthStore(
    (s) => ({ login: s.login, logout: s.logout }),
    shallow
  );
  const { setItems } = useItemsStore((s) => ({ setItems: s.setItems }), shallow);
  const { updateDataUser } = useUserStore((s) => ({ updateDataUser: s.updateDataUser }), shallow);
  const location = useLocation();

  const logoutTimer = useRef<number>();
  const refreshModalTimer = useRef<number>();

  useEffect(() => {
    const token = localStorage.getItem('authToken') || tokenFromURL;
    if (token) {
      login(token);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitData = async () => {
    try {
      setLoading(true);
      const response: InitDataDto = await GetInitDataService();
      const firstRoleId = Number(response.userRoles?.[0]?.roleId || 0);
      setUserRole(firstRoleId);
      initDataStore.setRolSelected(firstRoleId);
      initDataStore.setInitData(response);
      updateDataUser(response.user);

      const mapped: ItemDto[] = response.rolePermissions.map((rp) => ({
        id: parseInt(rp.id, 10),
        itemParentId: undefined,
        moduleId: 0,
        code: null,
        name: rp.permissions.name,
        route: rp.permissions.route || '',
        icon: rp.permissions.icon || '',
        cssClass: '',
        order: rp.permissions.order,
        showMenu: 1,
        isReport: 0,
        permissions: [],
        children: [],
        rolePermissions: null,
        domainUrl: '',
      }));
      setItems(mapped);

      if (location.pathname === '/' || location.pathname === '/login') {
        const firstRoute = mapped[0]?.route || '/not-authorized';
        navigate(firstRoute, { replace: true });
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading init data:', err);
      logout();
      setLoading(false);
    }
  };

  const scheduleExpirationPrompt = (exp: number) => {
    const now = Date.now();
    const expirationTime = exp * 1000;

    if (refreshModalTimer.current) clearTimeout(refreshModalTimer.current);

    if (expirationTime <= now) {
      // Ya está expirado -> abre modal de una
      setShowRefreshModal(true);
    } else {
      // Programa abrir modal exactamente en la expiración
      const ms = expirationTime - now;
      refreshModalTimer.current = window.setTimeout(() => {
        setShowRefreshModal(true);
      }, ms);
    }
  };

  const handleSessionExpired = () => {
    toast.error(
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AppGetIcon name="xCircleFill" />
        {'Sesión expirada'}
      </div>,
      { icon: false }
    );
    logout();
  };



  const scheduleLogout = (exp: number) => {
    const msLeft = exp * 1000 - Date.now();

    if (logoutTimer.current) clearTimeout(logoutTimer.current);

    if (msLeft <= 0) {
      logout();
    } else {
      logoutTimer.current = window.setTimeout(() => {
        logout();
      }, msLeft);
    }
  };

  const refreshToken = async () => {
    setIsRefreshingToken(true);
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken) {
        toast.error('No se encontró el token de refresco. Por favor, inicia sesión de nuevo.');
        logout();
        return;
      }

      const response = await postRefreshTokenService.run({
        refreshToken: storedRefreshToken,
      });

      const accessToken = response.accessToken;
      const refreshToken = response.refreshToken;

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Re-loguear para re-programar la nueva expiración:
      await login(accessToken);

      setShowRefreshModal(false);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {'¡Token refrescado exitosamente!'}
        </div>,
        { icon: false }
      );
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Error al refrescar el token";
      toast.error(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="xCircleFill" />
          {msg}
        </div>,
        { icon: false }
      );
      logout();
    } finally {
      setIsRefreshingToken(false);
    }
  };



  const login = async (token: string) => {
    try {
      const { exp } = jwtDecode<JwtPayload>(token);

      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (refreshModalTimer.current) clearTimeout(refreshModalTimer.current);

      // ❌ No mostramos más un modal con countdown anticipado
      // ❌ No programamos logout inmediato al expirar
      // ✅ Programamos abrir el modal justo en la expiración:
      scheduleExpirationPrompt(exp!);

      localStorage.setItem('authToken', token);
      storeLogin(token);
      await loadInitData();
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Token inválido o expirado:', err);
      logout();
      setLoading(false);
    }
  };


  const logout = () => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (refreshModalTimer.current) clearTimeout(refreshModalTimer.current);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    storeLogout();
    setIsAuthenticated(false);
    setUserRole(0);
    setShowRefreshModal(false);
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        loading,
        login,
        logout,
        refreshModal: {
          isOpen: showRefreshModal,
          isLoading: isRefreshingToken,
          timeRemaining: modalTimeRemaining,
          close: () => setShowRefreshModal(false),
          refresh: refreshToken,
          onSessionExpired: handleSessionExpired,
        },
      }}
    >
      {children}
      {/* ❌ Quita el TokenRefreshModal de aquí */}
    </AuthContext.Provider>

  );
};

export const useAuth = () => useContext(AuthContext);
